<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveEventGuestsStore } from '../../lib/convexRealtime';
  import {
    deleteAdminTicket,
    importAdminTickets,
    setTicketCheckedIn,
    setTicketInviteAllowance,
    upsertAdminTicket
  } from '../../lib/convexApi';
  import { buildGuestCsv } from '../../lib/guestCsv';
  import type { EventGuestEntry } from '../../lib/types';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let eventId: string;
  export let eventName: string;

  let store: ReturnType<typeof liveEventGuestsStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedId: string | null = null;

  let guests: EventGuestEntry[] = [];
  let loading = true;
  let error: string | null = null;

  let pendingTicketId: string | null = null;
  let actionError: string | null = null;

  let search = '';
  // 'redeemed' = on the guest list; 'checkedin' = through the door.
  let statusFilter: 'all' | 'issued' | 'redeemed' | 'checkedin' = 'all';

  let showAddForm = false;
  let addName = '';
  let addEmail = '';
  // Manager adds are comps — on the guest list immediately (default on).
  let addOnGuestList = true;
  let adding = false;

  let showImport = false;
  let importText = '';
  let importing = false;
  let importSummary: string | null = null;

  let confirmRemoveId: string | null = null;

  $: if (eventId && eventId !== subscribedId) {
    subscribedId = eventId;
    search = '';
    statusFilter = 'all';
    showAddForm = false;
    addOnGuestList = true;
    showImport = false;
    importSummary = null;
    actionError = null;
    unsubscribe?.();
    store?.dispose();
    store = liveEventGuestsStore(eventId);
    unsubscribe = store.subscribe(state => {
      guests = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
  }

  onDestroy(() => {
    unsubscribe?.();
    store?.dispose();
  });

  const ORIGIN_LABELS: Record<string, string> = {
    direct: 'Guest list',
    admin: 'Guest list',
    import: 'Guest list',
    personal: 'Guest list',
    self: 'Member',
    invite: 'Invited',
    application: 'Applied'
  };

  function setStatusFilter(value: string) {
    statusFilter = value as typeof statusFilter;
  }

  $: normalizedSearch = search.trim().toLowerCase();
  $: filtered = guests.filter(guest => {
    if (statusFilter === 'checkedin' && !guest.checkedInAt) return false;
    if (statusFilter !== 'all' && statusFilter !== 'checkedin' && guest.status !== statusFilter) {
      return false;
    }
    if (!normalizedSearch) return true;
    const haystacks = [
      guest.person?.name ?? '',
      guest.person?.email ?? '',
      guest.code ?? '',
      guest.invitedBy?.personName ?? ''
    ];
    return haystacks.some(value => value.toLowerCase().includes(normalizedSearch));
  });

  // On the guest list (paid or comped) — capacity-bearing membership.
  $: onListCount = guests.filter(guest => guest.status === 'redeemed').length;
  // Through the door — the independent check-in fact.
  $: checkedInCount = guests.filter(guest => guest.checkedInAt).length;

  function normalizeName(value: string): string {
    return value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  async function run(ticketId: string | null, action: () => Promise<unknown>) {
    pendingTicketId = ticketId;
    actionError = null;
    try {
      await action();
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Action failed.';
    } finally {
      pendingTicketId = null;
    }
  }

  function toggleCheckIn(guest: EventGuestEntry) {
    void run(guest.ticketId, () => setTicketCheckedIn(guest.ticketId, !guest.checkedInAt));
  }

  function removeGuest(guest: EventGuestEntry) {
    confirmRemoveId = null;
    void run(guest.ticketId, () => deleteAdminTicket(guest.ticketId));
  }

  async function addGuest() {
    const name = addName.trim();
    if (!name) {
      actionError = 'Guest name is required.';
      return;
    }
    const duplicate = guests.find(guest => normalizeName(guest.person?.name ?? '') === normalizeName(name));
    if (duplicate) {
      actionError = `"${duplicate.person?.name}" is already on the guest list.`;
      return;
    }
    adding = true;
    actionError = null;
    try {
      await upsertAdminTicket({
        eventId,
        person: { name, email: addEmail.trim() || undefined },
        redeemed: addOnGuestList
      });
      addName = '';
      addEmail = '';
      addOnGuestList = true;
      showAddForm = false;
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Could not add the guest.';
    } finally {
      adding = false;
    }
  }

  async function runImport() {
    const lines = importText
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
    const existing = new Set(guests.map(guest => normalizeName(guest.person?.name ?? '')));
    const seen = new Set<string>();
    const ready: string[] = [];
    let skipped = 0;
    for (const line of lines) {
      const key = normalizeName(line);
      if (!key || existing.has(key) || seen.has(key)) {
        skipped += 1;
        continue;
      }
      seen.add(key);
      ready.push(line);
    }
    if (ready.length === 0) {
      importSummary = `Nothing to import — ${skipped} name${skipped === 1 ? '' : 's'} skipped (duplicates or empty).`;
      return;
    }
    importing = true;
    actionError = null;
    try {
      await importAdminTickets(eventId, ready);
      importSummary = `Imported ${ready.length} guest${ready.length === 1 ? '' : 's'}${
        skipped > 0 ? `, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}` : ''
      }.`;
      importText = '';
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Import failed.';
    } finally {
      importing = false;
    }
  }

  function exportCsv() {
    const csv = buildGuestCsv(filtered, ORIGIN_LABELS);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${eventId}-guests.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function changeAllowance(guest: EventGuestEntry, raw: string) {
    await run(guest.ticketId, () => setTicketInviteAllowance(guest.ticketId, raw === '' ? null : Number(raw)));
  }

  function onAllowanceChange(guest: EventGuestEntry) {
    return (domEvent: Event) => {
      const raw = (domEvent.target as HTMLInputElement).value;
      void changeAllowance(guest, raw);
    };
  }
</script>

<section class="guests surface surface--sunken" aria-label={`Guests for ${eventName}`}>
  <header class="guests__header">
    <h4>Guests · {eventName}</h4>
    <span class="guests__stats">
      {onListCount} on the list · {checkedInCount} checked in
    </span>
  </header>

  <div class="guests__toolbar">
    <input
      type="search"
      placeholder="Search name, email, code…"
      bind:value={search}
      class="guests__search"
    />
    <div class="guests__chips" role="group" aria-label="Status filter">
      {#each [['all', 'All'], ['redeemed', 'On the list'], ['issued', 'Not on list'], ['checkedin', 'Checked in']] as [value, label] (value)}
        <button
          type="button"
          class="guests__chip"
          class:guests__chip--active={statusFilter === value}
          on:click={() => setStatusFilter(value)}
        >
          {label}
        </button>
      {/each}
    </div>
    <div class="guests__tools">
      <button type="button" class="secondary" on:click={() => { showAddForm = !showAddForm; showImport = false; }}>
        {showAddForm ? 'Close' : 'Add guest'}
      </button>
      <button type="button" class="secondary" on:click={() => { showImport = !showImport; showAddForm = false; importSummary = null; }}>
        {showImport ? 'Close import' : 'Import names'}
      </button>
      <button type="button" class="ghost" on:click={exportCsv} disabled={filtered.length === 0}>
        Export CSV
      </button>
    </div>
  </div>

  {#if actionError}
    <Notice variant="error">{actionError}</Notice>
  {/if}

  {#if showAddForm}
    <form class="guests__add surface" on:submit|preventDefault={addGuest}>
      <input type="text" placeholder="Full name" bind:value={addName} required />
      <input type="email" placeholder="Email (optional)" bind:value={addEmail} />
      <label class="guests__add-check">
        <input type="checkbox" bind:checked={addOnGuestList} />
        On the guest list
      </label>
      <button type="submit" class="accent" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
    </form>
  {/if}

  {#if showImport}
    <div class="guests__import surface">
      <p class="guests__hint">One name per line. Names already on the list are skipped.</p>
      <textarea rows="6" bind:value={importText} placeholder={'Jane Doe\nJohn Smith'}></textarea>
      {#if importSummary}
        <Notice>{importSummary}</Notice>
      {/if}
      <button type="button" class="accent" on:click={runImport} disabled={importing || !importText.trim()}>
        {importing ? 'Importing…' : 'Import'}
      </button>
    </div>
  {/if}

  {#if loading && guests.length === 0}
    <LoadingBlock label="Loading guests…" />
  {:else if error}
    <Notice variant="error">Could not load guests: {error}</Notice>
  {:else if guests.length === 0}
    <p class="guests__empty">No tickets yet.</p>
  {:else if filtered.length === 0}
    <p class="guests__empty">No guests match the current filter.</p>
  {:else}
    <ul>
      {#each filtered as guest (guest.ticketId)}
        <li class="guests__row" class:guests__row--redeemed={guest.checkedInAt}>
          <div class="guests__info">
            <span class="guests__name">
              {guest.person?.name ?? 'Unknown'}
              <span class="guests__origin guests__origin--{guest.origin}">
                {ORIGIN_LABELS[guest.origin] ?? guest.origin}
              </span>
              {#if guest.status === 'redeemed'}
                <span class="guests__onlist">on list</span>
              {/if}
              {#if guest.checkedInAt}
                <span class="guests__checked">✓ in</span>
              {/if}
            </span>
            <span class="guests__meta">
              {guest.code}
              {#if guest.person?.email}
                · {guest.person.email}
              {/if}
              {#if guest.invitedBy}
                · invited by {guest.invitedBy.personName}
              {/if}
            </span>
          </div>

          <div class="guests__actions">
            <label class="guests__allowance">
              +1s
              <input
                type="number"
                min="1"
                placeholder="def"
                value={guest.inviteAllowance ?? ''}
                disabled={pendingTicketId === guest.ticketId}
                on:change={onAllowanceChange(guest)}
              />
            </label>
            <button
              type="button"
              class="secondary guests__action"
              disabled={pendingTicketId === guest.ticketId}
              on:click={() => toggleCheckIn(guest)}
            >
              {guest.checkedInAt ? 'Undo check-in' : 'Check in'}
            </button>
            {#if confirmRemoveId === guest.ticketId}
              <button
                type="button"
                class="guests__action guests__danger"
                disabled={pendingTicketId === guest.ticketId}
                on:click={() => removeGuest(guest)}
              >
                Confirm remove
              </button>
              <button type="button" class="ghost guests__action" on:click={() => (confirmRemoveId = null)}>
                Keep
              </button>
            {:else}
              <button
                type="button"
                class="ghost guests__action"
                disabled={pendingTicketId === guest.ticketId}
                on:click={() => (confirmRemoveId = guest.ticketId)}
              >
                Remove
              </button>
            {/if}
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .guests {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
  }

  .guests__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .guests h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .guests__stats {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .guests__toolbar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .guests__search {
    flex: 1 1 220px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .guests__chips {
    display: flex;
    gap: var(--space-1);
  }

  .guests__chip {
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
    cursor: pointer;
  }

  .guests__chip--active {
    color: var(--color-text-primary);
    border-color: rgb(var(--accent-rgb) / 0.5);
    background: rgb(var(--accent-rgb) / 0.08);
  }

  .guests__tools {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .guests__add {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    padding: var(--space-3);
    border-radius: var(--radius-md);
    flex-wrap: wrap;
  }

  .guests__add input[type='text'],
  .guests__add input[type='email'] {
    flex: 1 1 180px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .guests__add-check {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .guests__import {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-3);
    border-radius: var(--radius-md);
  }

  .guests__import textarea {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
    font-family: inherit;
    resize: vertical;
  }

  .guests__import button {
    align-self: flex-start;
  }

  .guests__hint {
    margin: 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .guests ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .guests__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
    flex-wrap: wrap;
  }

  .guests__row--redeemed {
    border-color: rgba(78, 224, 195, 0.25);
  }

  .guests__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .guests__name {
    font-weight: 600;
    font-size: var(--font-size-sm);
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .guests__origin {
    font-size: var(--font-size-2xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 1px var(--space-2);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    color: var(--color-text-muted);
  }

  .guests__origin--self {
    color: var(--color-accent-neon-400);
    border-color: rgba(78, 224, 195, 0.35);
  }

  .guests__origin--invite {
    color: var(--color-text-amber);
    border-color: rgb(var(--accent-rgb) / 0.4);
  }

  .guests__checked {
    font-size: var(--font-size-2xs);
    color: var(--color-accent-neon-400);
  }

  .guests__onlist {
    font-size: var(--font-size-2xs);
    color: var(--color-text-muted);
  }

  .guests__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .guests__actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .guests__action {
    font-size: var(--font-size-xs);
    padding: var(--space-1) var(--space-3);
  }

  .guests__danger {
    border: 1px solid rgba(255, 107, 107, 0.5);
    border-radius: var(--radius-pill);
    background: rgba(255, 107, 107, 0.1);
    color: #ff8f8f;
    cursor: pointer;
  }

  .guests__allowance {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    flex-shrink: 0;
  }

  .guests__allowance input {
    width: 64px;
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .guests__empty {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }
</style>
